import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Leaf, 
  ArrowRight, 
  CheckCircle, 
  TrendingUp, 
  Shield,
  Eye,
  Play,
  Droplets,
  Wind,
  Battery,
  Recycle
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const GreenHydrogenPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  
  

  const challenges = [
    'Monitoring water levels and quality for hydrogen production processes',
    'Ensuring equipment reliability and preventing failures in electrolysis systems',
    'Maintaining worker safety in high-pressure hydrogen environments',
    'Managing complex renewable energy integration and storage systems',
    'Tracking environmental compliance and sustainability metrics',
    'Optimizing production efficiency across distributed green hydrogen facilities'
  ];

  const solutions = [
    {
      name: 'Green H2',
      path: '/products/greenh2',
      description: 'Produce clean hydrogen from renewable electricity and water, eliminating fossil inputs.',
      benefits: ['Industrial decarbonization', 'Energy storage', 'Renewable electrolysis']
    },
    // {
    //   name: 'Equipment Failure Detection',
    //   path: '/products/ai#equipment-failure-detection',
    //   description: 'Predictive maintenance and monitoring for critical hydrogen production and storage equipment.',
    //   benefits: ['Predictive Analytics', 'System Reliability', 'Downtime Prevention']
    // },
    // {
    //   name: 'PPE Compliance Monitoring',
    //   path: '/products/ai#ppe-compliance-monitoring',
    //   description: 'Real-time safety monitoring for workers in hydrogen production and handling environments.',
    //   benefits: ['Safety Compliance', 'Risk Mitigation', 'Incident Prevention']
    // }
  ];

  const keyFeatures = [
    {
      icon: Leaf,
      title: 'Sustainability-First Approach',
      description: 'Solutions designed specifically for renewable energy and sustainable hydrogen production operations.'
    },
    {
      icon: Droplets,
      title: 'Advanced Water Management',
      description: 'Comprehensive monitoring and optimization of water resources critical to hydrogen production.'
    },
    {
      icon: Battery,
      title: 'Energy Storage Integration',
      description: 'Intelligent systems that optimize hydrogen production with renewable energy storage and distribution.'
    },
    {
      icon: Shield,
      title: 'Safety & Environmental Focus',
      description: 'Robust safety monitoring and environmental compliance systems for clean energy operations.'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Production Efficiency',
      description: 'Optimized hydrogen production processes and resource management deliver significant efficiency improvements.',
      metric: '70% efficiency gain'
    },
    {
      icon: Droplets,
      title: 'Water Conservation',
      description: 'Advanced monitoring and management systems reduce water waste and optimize resource utilization.',
      metric: '50% water savings'
    },
    {
      icon: Shield,
      title: 'Safety Performance',
      description: 'Comprehensive safety monitoring and compliance systems drastically reduce operational risks.',
      metric: '85% safety improvement'
    },
    {
      icon: Recycle,
      title: 'Environmental Impact',
      description: 'Sustainable operations and environmental monitoring ensure minimal ecological footprint.',
      metric: '90% emission reduction'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 7 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-5 h-5 bg-green-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 6,
                delay: i * 0.4,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-green-500 to-emerald-600 neon-glow">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-green-400 uppercase tracking-wide">Sustainable Energy Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Green <span className="bg-linear-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">Hydrogen</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-green-300"
              >
                Leading the Clean Energy Revolution
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                Green hydrogen represents the future of clean energy, offering a sustainable solution for energy storage, 
                industrial processes, and transportation. As the industry rapidly scales, hydrogen production facilities 
                require sophisticated monitoring, safety systems, and resource optimization technologies. At amasQIS.ai, 
                we provide specialized IoT, AI, and automation solutions designed specifically for green hydrogen production, 
                storage, and distribution operations.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-green-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
                >
                  Power the Future
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-green-500 to-emerald-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/green-h2.png'
                    alt="Green Hydrogen Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-green-500/20 rounded-lg backdrop-blur-sm">
                          <Leaf className="w-5 h-5 text-green-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Hydrogen Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-green-500">
                    <Wind className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">H2</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in Green Hydrogen Production
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Green hydrogen operations require specialized solutions for water management, equipment reliability, and environmental safety.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <Droplets className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solutions for Green Hydrogen
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Specialized clean energy solutions designed to optimize hydrogen production, ensure safety, and maximize environmental benefits.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group h-full"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer h-full flex flex-col"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-green-400 group-hover:text-green-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Green Hydrogen Leaders Choose amasQIS.ai
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Our solutions are engineered for the unique requirements of sustainable hydrogen production and clean energy operations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Clean Energy Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your hydrogen operations with quantifiable improvements in efficiency, sustainability, and environmental performance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-green-500/20">
                      <Icon className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-emerald-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Lead the Hydrogen Revolution?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join pioneering clean energy companies that trust amasQIS.ai to deliver innovative, sustainable hydrogen production solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-green-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
              >
                Power the Future
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { GreenHydrogenPage };
